<template>
	<div
		ref="el"
		style="touch-action: none"
		class="fixed flex flex-col justify-between select-none cursor-move z-31 duration-0 bg-gray-900 shadow-lg/40 text-white w-64 h-32 rounded-lg p-2"
		:style="style"
	>
		<div class="flex flex-row justify-between items-center">
			<span>{{ getTitle(meeting) }}</span>
			<AppButton icon="stop" variant="danger" @click="emit('stop')">
				Stop
			</AppButton>
		</div>
		<span>{{ displayTime }}</span>
	</div>
</template>

<script setup lang="ts">
import { executeTransition, useDraggable } from "@vueuse/core";
import { getTitle } from "@/helpers/conversationHelpers";
import type { Row } from "@rocicorp/zero";
import type { Schema } from "@loanlink-nl/zero";
import type { MaybeRefOrGetter } from "vue";
import { onMounted, onUnmounted, ref, toValue, useTemplateRef } from "vue";

const props = defineProps<{
	meeting: Row<Schema["tables"]["conversationTable"]>;
	startedAt?: number;
}>();

const emit = defineEmits<{
	(e: "stop"): void;
}>();

const el = useTemplateRef("el");
const padding = 20;

const { displayTime } = useTimer(() => props.startedAt);

function moveInScreen(durationMs = 100) {
	if (!el.value) {
		return;
	}

	const { width, height } = el.value.getBoundingClientRect();

	const maxX = innerWidth - width - padding;
	const maxY = innerHeight - height - padding;

	let newX = x.value;
	let newY = y.value;

	if (newX > maxX) {
		newX = maxX;
	}

	if (newY > maxY) {
		newY = maxY;
	}

	if (newX < padding) {
		newX = padding;
	}

	if (newY < padding) {
		newY = padding;
	}

	executeTransition(x, x.value, newX, {
		duration: durationMs,
		transition: [0.2, 0.0, 0.2, 1.0],
	});

	executeTransition(y, y.value, newY, {
		duration: durationMs,
		transition: [0.2, 0.0, 0.2, 1.0],
	});
}

onMounted(() => {
	moveInScreen(0);
});

const { x, y, style } = useDraggable(el, {
	initialValue: { x: innerWidth, y: innerHeight },
	preventDefault: true,
	onEnd: () => {
		moveInScreen();
	},
});

function useTimer(startTime: MaybeRefOrGetter<number | undefined>) {
	const displayTime = ref("00:00");
	let animationId: number | null = null;

	const updateTimer = () => {
		const time = toValue(startTime);
		if (time) {
			const elapsed = Date.now() - time;
			const minutes = Math.floor(elapsed / 60000);
			const seconds = Math.floor((elapsed % 60000) / 1000);

			displayTime.value = `${minutes.toString().padStart(2, "0")}:${seconds
				.toString()
				.padStart(2, "0")}`;
		}

		animationId = requestAnimationFrame(updateTimer);
	};

	onMounted(() => {
		updateTimer();
	});

	onUnmounted(() => {
		if (animationId) {
			cancelAnimationFrame(animationId);
		}
	});

	return { displayTime };
}

function handleResize() {
	moveInScreen(0);
}

onMounted(() => {
	window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
	window.removeEventListener("resize", handleResize);
});
</script>
